(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-reviews-carousel';
  var CARD_SELECTOR = '.oddit-section-reviews-carousel__card';
  var DRAG_THRESHOLD = 5; // px before a press becomes a drag (and suppresses click)
  var sectionState = new Map();

  function initSection(section) {
    if (!section) return;
    if (sectionState.has(section)) destroySection(section);

    var track = section.querySelector('.oddit-section-reviews-carousel__track');
    var segments = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-reviews-carousel__progress-segment')
    );

    if (!track || segments.length === 0) return;

    var state = {
      scrollRafId: null,
      // loop (desktop, transform-based)
      loopEnabled: false,
      clones: [],
      originals: [],
      allCards: [],
      setWidth: 0,
      t0: 0,
      viewLeft: 0,          // wrapper offsetLeft (shared offsetParent origin)
      offset: 0,            // current translate amount (px, positive = scrolled forward)
      centeredOriginal: 0,
      // drag
      dragging: false,
      dragMoved: false,
      pointerId: null,
      startX: 0,
      startOffset: 0,
      lastX: 0,
      lastT: 0,
      velocity: 0,
      wheelTimer: null
    };
    sectionState.set(section, state);

    // ---- helpers --------------------------------------------------------

    function viewportWidth() {
      return track.parentNode.getBoundingClientRect().width;
    }

    // Card centres (offsetLeft, unaffected by the track transform) and the
    // wrapper's own offset share the same offsetParent, so card-left relative
    // to the wrapper is simply (card.offsetLeft - state.viewLeft).
    function offsetToCenter(card) {
      return card.offsetLeft + card.offsetWidth / 2 - state.viewLeft - viewportWidth() / 2;
    }

    function render(animate) {
      track.style.transition = animate ? 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      track.style.transform = 'translate3d(' + (-state.offset) + 'px, 0, 0)';
    }

    // Keep offset inside [t0, t0 + setWidth) by jumping whole sets. Because
    // each set is a pixel-identical copy, the jump is visually seamless.
    function wrapOffset() {
      if (state.setWidth <= 0) return;
      while (state.offset < state.t0) state.offset += state.setWidth;
      while (state.offset >= state.t0 + state.setWidth) state.offset -= state.setWidth;
    }

    // ---- build / teardown the loop -------------------------------------

    function prepClone(card) {
      var clone = card.cloneNode(true);
      clone.classList.add('oddit-section-reviews-carousel__card--clone');
      clone.classList.remove('oddit-reveal');
      Array.prototype.slice.call(clone.classList).forEach(function (cls) {
        if (cls.indexOf('oddit-reveal') === 0) clone.classList.remove(cls);
      });
      clone.setAttribute('aria-hidden', 'true');
      Array.prototype.slice.call(
        clone.querySelectorAll('a, button, [tabindex]')
      ).forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      Array.prototype.slice.call(
        clone.querySelectorAll('[data-setting-id]')
      ).forEach(function (el) {
        el.removeAttribute('data-setting-id');
        el.removeAttribute('data-placeholder');
      });
      return clone;
    }

    function buildLoop() {
      if (state.loopEnabled) return;
      var originals = Array.prototype.slice.call(track.querySelectorAll(CARD_SELECTOR));
      if (originals.length < 2) return;

      var clones = [];
      var lead = document.createDocumentFragment();
      var trail = document.createDocumentFragment();
      originals.forEach(function (card) {
        var before = prepClone(card);
        var after = prepClone(card);
        clones.push(before, after);
        lead.appendChild(before);
        trail.appendChild(after);
      });
      track.insertBefore(lead, originals[0]);
      track.appendChild(trail);

      state.originals = originals;
      state.clones = clones;
      state.allCards = Array.prototype.slice.call(track.querySelectorAll(CARD_SELECTOR));
      section.classList.add('oddit-section-reviews-carousel--loop');
      state.loopEnabled = true;

      measureLoop();
      state.centeredOriginal = 0;
      state.offset = state.t0;
      render(false);
      updateProgress();

      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('wheel', onWheel, { passive: false });
      track.addEventListener('click', onClickCapture, true);
      track.addEventListener('dragstart', onDragStart);
    }

    function measureLoop() {
      var originals = state.originals;
      var n = originals.length;
      if (!n) return;
      // One set spans the n leading clones that precede the first original.
      state.setWidth = originals[0].offsetLeft - state.allCards[0].offsetLeft;
      state.viewLeft = track.parentNode.offsetLeft;
      state.t0 = offsetToCenter(originals[0]);
    }

    function teardownLoop() {
      if (!state.loopEnabled) return;
      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('wheel', onWheel);
      track.removeEventListener('click', onClickCapture, true);
      track.removeEventListener('dragstart', onDragStart);
      endDrag();
      state.clones.forEach(function (clone) {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
      state.clones = [];
      state.originals = [];
      state.allCards = [];
      state.loopEnabled = false;
      state.setWidth = 0;
      track.style.transition = '';
      track.style.transform = '';
      section.classList.remove('oddit-section-reviews-carousel--loop');
    }

    // ---- drag -----------------------------------------------------------

    function onPointerDown(e) {
      if (e.button != null && e.button !== 0) return; // primary button only
      // Normalise into the canonical band first — this teleport is instant
      // (no transition) and seamless because each set is identical, so the
      // drag that follows can never animate across a wrap boundary.
      wrapOffset();
      render(false);
      state.dragging = true;
      state.dragMoved = false;
      state.pointerId = e.pointerId;
      state.startX = state.lastX = e.clientX;
      state.startOffset = state.offset;
      state.lastT = e.timeStamp;
      state.velocity = 0;
      track.classList.add('is-dragging');
      render(false);
      if (track.setPointerCapture) {
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', onPointerUp);
      track.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(e) {
      if (!state.dragging) return;
      var dx = e.clientX - state.startX;
      if (!state.dragMoved && Math.abs(dx) > DRAG_THRESHOLD) state.dragMoved = true;
      state.offset = state.startOffset - dx;
      wrapOffset();
      render(false);
      // Track instantaneous velocity for release momentum.
      var dt = e.timeStamp - state.lastT;
      if (dt > 0) state.velocity = (e.clientX - state.lastX) / dt; // px per ms
      state.lastX = e.clientX;
      state.lastT = e.timeStamp;
      scheduleProgress();
    }

    function onPointerUp() {
      if (!state.dragging) return;
      endDrag();
      // Project a little momentum, then snap to the nearest card centre.
      var projected = state.offset - state.velocity * 120;
      snapToOffset(projected);
    }

    function endDrag() {
      if (state.pointerId != null && track.releasePointerCapture) {
        try { track.releasePointerCapture(state.pointerId); } catch (err) {}
      }
      state.dragging = false;
      state.pointerId = null;
      track.classList.remove('is-dragging');
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', onPointerUp);
      track.removeEventListener('pointercancel', onPointerUp);
    }

    // Stop the browser's native image/link drag (the "ghost image") from
    // hijacking the pointer-drag when the press starts on a card image — without
    // this, dragging the images themselves wouldn't scroll the carousel (RC-4).
    function onDragStart(e) {
      e.preventDefault();
    }

    // Suppress the click that follows a drag (so dragging never opens a link).
    function onClickCapture(e) {
      if (state.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        state.dragMoved = false;
      }
    }

    // ---- wheel ----------------------------------------------------------

    function onWheel(e) {
      // Only hijack predominantly-horizontal wheel/trackpad gestures; let
      // vertical scrolling fall through so the page can still scroll.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      state.offset += e.deltaX;
      wrapOffset();
      render(false);
      scheduleProgress();
      if (state.wheelTimer) clearTimeout(state.wheelTimer);
      state.wheelTimer = setTimeout(function () {
        snapToOffset(state.offset);
      }, 90);
    }

    // ---- snapping / progress -------------------------------------------

    // Snap so the nearest card centre lands in the middle. Cards are uniform,
    // so their centre offsets form a lattice spaced `step` apart anchored at
    // t0. Snapping to the nearest lattice point keeps the animation short and,
    // crucially, never crosses a wrap boundary — so it can't rewind.
    function snapToOffset(desiredOffset) {
      var n = state.originals.length;
      if (!n || state.setWidth <= 0) return;
      var step = state.setWidth / n;
      state.offset = state.t0 + Math.round((desiredOffset - state.t0) / step) * step;
      render(true);
      updateProgress();
    }

    function scheduleProgress() {
      if (state.scrollRafId !== null) return;
      state.scrollRafId = requestAnimationFrame(function () {
        updateProgress();
        state.scrollRafId = null;
      });
    }

    function setActiveSegment(activeIndex) {
      segments.forEach(function (seg, i) {
        if (i === activeIndex) {
          seg.classList.add('oddit-section-reviews-carousel__progress-segment--active');
        } else {
          seg.classList.remove('oddit-section-reviews-carousel__progress-segment--active');
        }
      });
    }

    function updateProgress() {
      if (state.loopEnabled) {
        var cards = state.allCards;
        var n = state.originals.length;
        if (!cards.length || !n) return;
        var center = state.offset + state.viewLeft + viewportWidth() / 2;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (card, i) {
          var cc = card.offsetLeft + card.offsetWidth / 2;
          var d = Math.abs(cc - center);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        var originalIndex = ((best - n) % n + n) % n;
        state.centeredOriginal = originalIndex;
        setActiveSegment(Math.min(
          segments.length - 1,
          Math.round((originalIndex / Math.max(1, n - 1)) * (segments.length - 1))
        ));
        return;
      }
      // Mobile native-scroll path.
      var scrollLeft = track.scrollLeft;
      var scrollWidth = track.scrollWidth - track.clientWidth;
      var ratio = scrollWidth > 0 ? scrollLeft / scrollWidth : 0;
      setActiveSegment(Math.min(
        segments.length - 1,
        Math.round(ratio * (segments.length - 1))
      ));
    }

    // ---- native scroll (mobile) ----------------------------------------

    function onScroll() {
      if (state.loopEnabled) return; // desktop uses transform, not scroll
      scheduleProgress();
    }
    track.addEventListener('scroll', onScroll, { passive: true });

    // ---- progress segment navigation -----------------------------------

    segments.forEach(function (seg, index) {
      seg.addEventListener('click', function () {
        if (state.loopEnabled) {
          var n = state.originals.length;
          var originalIndex = Math.round(
            (index / Math.max(1, segments.length - 1)) * (n - 1)
          );
          var target = state.allCards[n + originalIndex];
          if (target && state.setWidth > 0) {
            // Pick the equivalent centre offset nearest the current position so
            // the animation takes the shortest path and never rewinds.
            var d = offsetToCenter(target);
            d += Math.round((state.offset - d) / state.setWidth) * state.setWidth;
            state.offset = d;
            render(true);
            updateProgress();
          }
          return;
        }
        var scrollWidth = track.scrollWidth - track.clientWidth;
        if (segments.length <= 1) return;
        track.scrollTo({
          left: scrollWidth * (index / (segments.length - 1)),
          behavior: 'smooth'
        });
      });
      seg.setAttribute('tabindex', '0');
      seg.setAttribute('role', 'button');
      seg.setAttribute('aria-label', 'Go to slide group ' + (index + 1));
      seg.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          seg.click();
        }
      });
    });

    // ---- enable loop ----------------------------------------------------

    // The transform-based seamless loop runs at every breakpoint (mobile and
    // desktop) so the carousel loops on touch as well as pointer/wheel input.
    buildLoop();
    updateProgress();

    // Re-measure on resize, keeping the same card centred.
    var resizeObserver = new ResizeObserver(function () {
      if (state.loopEnabled) {
        measureLoop();
        var card = state.allCards[state.originals.length + state.centeredOriginal];
        if (card) {
          state.offset = offsetToCenter(card);
          wrapOffset();
          render(false);
        }
      }
      updateProgress();
    });
    resizeObserver.observe(track.parentNode);
    state.resizeObserver = resizeObserver;

    state.onScroll = onScroll;
    state.track = track;
    state.teardownLoop = teardownLoop;
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;
    if (state.scrollRafId !== null) cancelAnimationFrame(state.scrollRafId);
    if (state.wheelTimer) clearTimeout(state.wheelTimer);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (state.track && state.onScroll) {
      state.track.removeEventListener('scroll', state.onScroll);
    }
    // Removes the pointer/wheel/click listeners, drops the clones and resets
    // the track transform — complete teardown for re-init or section unload.
    if (state.teardownLoop) state.teardownLoop();
    sectionState.delete(section);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
